import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import {
  Box,
  TextField,
  Button,
  Typography,
  Avatar,
  IconButton,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Send as SendIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';

interface Comment {
  id: number;
  post_id: number;
  author_id: number;
  author_name: string;
  content: string;
  created_at: string;
  updated_at: string;
}

interface PostCommentsProps {
  postId: number;
  commentsCount: number;
  onCommentAdded?: () => void;
}

const PostComments: React.FC<PostCommentsProps> = ({ postId, commentsCount, onCommentAdded }) => {
  const { token, user } = useAuth();
  const { t } = useTranslation();

  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showComments, setShowComments] = useState(false);

  useEffect(() => {
    if (showComments) {
      fetchComments();
    }
  }, [showComments, postId]);

  const fetchComments = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/posts/${postId}/comments`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error('Failed to fetch comments');

      const data = await response.json();
      if (data.success) {
        setComments(data.comments);
      }
    } catch (err: any) {
      console.error('Error fetching comments:', err);
      setError(err.message || 'Failed to load comments');
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    try {
      setSubmitting(true);
      setError(null);

      const response = await fetch(`/api/posts/${postId}/comments`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ content: newComment.trim() }),
      });

      if (!response.ok) throw new Error('Failed to add comment');

      const data = await response.json();
      if (data.success) {
        setComments([...comments, data.comment]);
        setNewComment('');
        if (onCommentAdded) onCommentAdded();
      }
    } catch (err: any) {
      console.error('Error adding comment:', err);
      setError(err.message || 'Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId: number) => {
    if (!window.confirm(t('classFeed.confirmDeleteComment'))) return;

    try {
      const response = await fetch(`/api/posts/${postId}/comments/${commentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error('Failed to delete comment');

      const data = await response.json();
      if (data.success) {
        setComments(comments.filter(c => c.id !== commentId));
        if (onCommentAdded) onCommentAdded();
      }
    } catch (err: any) {
      console.error('Error deleting comment:', err);
      setError(err.message || 'Failed to delete comment');
    }
  };

  const formatTimeAgo = (dateString: string) => {
    try {
      const now = new Date();
      const date = new Date(dateString);
      const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
      
      if (seconds < 60) return 'just now';
      const minutes = Math.floor(seconds / 60);
      if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
      const hours = Math.floor(minutes / 60);
      if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
      const days = Math.floor(hours / 24);
      if (days < 7) return `${days} day${days !== 1 ? 's' : ''} ago`;
      const weeks = Math.floor(days / 7);
      if (weeks < 4) return `${weeks} week${weeks !== 1 ? 's' : ''} ago`;
      const months = Math.floor(days / 30);
      if (months < 12) return `${months} month${months !== 1 ? 's' : ''} ago`;
      const years = Math.floor(days / 365);
      return `${years} year${years !== 1 ? 's' : ''} ago`;
    } catch {
      return new Date(dateString).toLocaleDateString();
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  return (
    <Box sx={{ mt: 2, borderTop: '1px solid #e0e0e0', pt: 2 }}>
      {/* Comments Toggle Button */}
      <Button
        onClick={() => setShowComments(!showComments)}
        variant="text"
        size="small"
        aria-expanded={showComments}
        aria-label={`${showComments ? t('classFeed.hideComments') : t('classFeed.viewComments')} - ${commentsCount} comments`}
        sx={{ textTransform: 'none', mb: 1 }}
      >
        {showComments ? t('classFeed.hideComments') : t('classFeed.viewComments')} 
        {` (${commentsCount})`}
      </Button>

      {showComments && (
        <>
          {error && (
            <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Comments List */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Box sx={{ mb: 2 }}>
              {comments.map((comment) => (
                <Box
                  key={comment.id}
                  sx={{
                    display: 'flex',
                    gap: 1.5,
                    mb: 2,
                    p: 2,
                    backgroundColor: '#f5f5f5',
                    borderRadius: 2
                  }}
                >
                  <Avatar sx={{ width: 32, height: 32, fontSize: '0.875rem' }}>
                    {getInitials(comment.author_name)}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {comment.author_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatTimeAgo(comment.created_at)}
                        </Typography>
                      </Box>
                      {user && comment.author_id === user.id && (
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteComment(comment.id)}
                          aria-label={`Delete comment by ${comment.author_name}`}
                          sx={{ color: 'error.main' }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {comment.content}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          )}

          {/* Add Comment Form */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <Avatar sx={{ width: 32, height: 32, fontSize: '0.875rem', mt: 1 }}>
              {user && getInitials(`${user.first_name} ${user.last_name}`)}
            </Avatar>
            <TextField
              fullWidth
              multiline
              rows={2}
              placeholder={t('classFeed.addComment')}
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleAddComment();
                }
              }}
              disabled={submitting}
              size="small"
            />
            <Button
              variant="contained"
              onClick={handleAddComment}
              disabled={!newComment.trim() || submitting}
              startIcon={submitting ? <CircularProgress size={16} /> : <SendIcon />}
              aria-label={submitting ? 'Posting comment' : 'Post comment'}
              sx={{ mt: 1 }}
            >
              {t('classFeed.postComment')}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default PostComments;

